import { FC } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { AffiliateSettings } from '../../../utils/affiliateSettings';

interface TrackingSettingsTabProps {
  affiliateSettings: AffiliateSettings;
  setAffiliateSettings: React.Dispatch<React.SetStateAction<AffiliateSettings>>;
  isSaving: boolean;
  onSave: () => void;
}

export const TrackingSettingsTab: FC<TrackingSettingsTabProps> = ({
  affiliateSettings,
  setAffiliateSettings,
  isSaving,
  onSave,
}) => {
  return (
    <Card id="tracking-settings">
      <Card.Header
        title={
          <div>
            <h3 className="text-sm font-semibold text-content-main">
              Pixels de Conversão & Conformidade Legal
            </h3>
            <p className="text-xs text-content-muted">
              Rastreamento de métricas externas e links institucionais para
              landing pages e vitrine
            </p>
          </div>
        }
        action={
          <Button
            variant="primary"
            size="sm"
            isLoading={isSaving}
            onClick={onSave}
          >
            Salvar Pixels
          </Button>
        }
      />
      <Card.Body className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Meta Pixel ID"
            value={affiliateSettings.metaPixelId || ''}
            onChange={(e) =>
              setAffiliateSettings({
                ...affiliateSettings,
                metaPixelId: e.target.value.trim(),
              })
            }
            placeholder="Ex: 123456789012345"
            helperText="Injetado no cabeçalho das landing pages"
          />
          <Input
            label="Google Analytics 4 (Measurement ID)"
            value={affiliateSettings.ga4MeasurementId || ''}
            onChange={(e) =>
              setAffiliateSettings({
                ...affiliateSettings,
                ga4MeasurementId: e.target.value.trim(),
              })
            }
            placeholder="Ex: G-XXXXXXXXXX"
            helperText="Rastreamento de visualizações e cliques"
          />
          <Input
            label="Link da Política de Privacidade"
            value={affiliateSettings.privacyPolicyUrl || ''}
            onChange={(e) =>
              setAffiliateSettings({
                ...affiliateSettings,
                privacyPolicyUrl: e.target.value.trim(),
              })
            }
            placeholder="https://seusite.com/privacidade"
            helperText="Exibido no rodapé da vitrine pública"
          />
        </div>

        <div className="border-t border-surface-border pt-2">
          <label className="mb-1 block text-xs font-semibold text-content-main">
            Formato Padrão do Link nas Mensagens do WhatsApp
          </label>
          <p className="mb-3 text-xs text-content-muted">
            Define como o link do produto será montado por padrão ao cadastrar
            novas ofertas. Você pode alterar individualmente em cada oferta.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div
              onClick={() =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  defaultLinkType: 'api',
                })
              }
              className={`cursor-pointer rounded-lg border p-3.5 transition-all ${
                (affiliateSettings.defaultLinkType || 'api') === 'api'
                  ? 'border-brand-500 bg-brand-500/5'
                  : 'hover:border-surface-border-strong border-surface-border bg-surface-subtle'
              }`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-content-main">
                  Link Rastreável da API (Recomendado)
                </span>
                <input
                  type="radio"
                  name="defaultLinkType"
                  checked={
                    (affiliateSettings.defaultLinkType || 'api') === 'api'
                  }
                  onChange={() =>
                    setAffiliateSettings({
                      ...affiliateSettings,
                      defaultLinkType: 'api',
                    })
                  }
                  className="h-4 w-4 text-brand-500"
                />
              </div>
              <p className="text-2xs text-content-muted">
                Exemplo:{' '}
                <code className="text-brand-500">
                  /r/{'{tenant}'}/{'{loja}'}/{'{codigo}'}
                </code>
                . Permite telemetria de cliques, subIDs automáticos por grupo e
                troca de destino pós-envio.
              </p>
            </div>

            <div
              onClick={() =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  defaultLinkType: 'marketplace',
                })
              }
              className={`cursor-pointer rounded-lg border p-3.5 transition-all ${
                affiliateSettings.defaultLinkType === 'marketplace'
                  ? 'border-brand-500 bg-brand-500/5'
                  : 'hover:border-surface-border-strong border-surface-border bg-surface-subtle'
              }`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-content-main">
                  Link Direto Nativo do Marketplace
                </span>
                <input
                  type="radio"
                  name="defaultLinkType"
                  checked={affiliateSettings.defaultLinkType === 'marketplace'}
                  onChange={() =>
                    setAffiliateSettings({
                      ...affiliateSettings,
                      defaultLinkType: 'marketplace',
                    })
                  }
                  className="h-4 w-4 text-brand-500"
                />
              </div>
              <p className="text-2xs text-content-muted">
                Exemplo:{' '}
                <code className="text-content-main">s.shopee.com.br/...</code>{' '}
                ou <code className="text-content-main">amzn.to/...</code>. Link
                nativo direto do vendedor no texto do WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
